import {
    Component,
    EventEmitter,
    inject,
    Inject,
    OnDestroy,
    OnInit,
    Output,
} from '@angular/core';
import {
    FormControl,
    FormGroup,
    FormsModule,
    NonNullableFormBuilder,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { NzButtonModule } from 'ng-zorro-antd/button';
import {
    NzFormControlComponent,
    NzFormDirective,
    NzFormItemComponent,
    NzFormModule,
} from 'ng-zorro-antd/form';
import { NzColDirective, NzRowDirective } from 'ng-zorro-antd/grid';
import { NzInputDirective } from 'ng-zorro-antd/input';
import { NzMessageModule, NzMessageService } from 'ng-zorro-antd/message';
import {
    NZ_MODAL_DATA,
    NzModalFooterDirective,
    NzModalRef,
    NzModalTitleDirective,
} from 'ng-zorro-antd/modal';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { Subject, takeUntil } from 'rxjs';
import { UserLoggedService } from 'src/app/core/services/userLogged.service';
import { PauseReasonsService } from 'src/app/private/services/pause-reasons.service';
import { PauseService } from 'src/app/private/services/pause.service';
import { ProductionOrderProcessesService } from 'src/app/private/services/production-order-processes.service';
import { LoaderComponent } from 'src/app/shared/components/loader/loader.component';
import { PauseReasonDto } from 'src/app/shared/dto/pause-reason.dto';
import { ProductionOrderProcessDto } from 'src/app/shared/dto/production-order-process.dto';
import { ProductionPauseDto } from 'src/app/shared/dto/production-pause.dto';
import { UserDto } from 'src/app/shared/dto/user.dto';

@Component({
    selector: 'app-modal-create-pause',
    templateUrl: './modal-create-pause.component.html',
    styleUrls: ['./modal-create-pause.component.scss'],
    standalone: true,
    imports: [
        NzModalTitleDirective,
        ReactiveFormsModule,
        FormsModule,
        NzFormDirective,
        NzRowDirective,
        NzFormItemComponent,
        NzColDirective,
        NzFormModule,
        NzFormControlComponent,
        NzInputDirective,
        NzSelectModule,
        NzInputDirective,
        NzModalFooterDirective,
        NzMessageModule,
        LoaderComponent,
        NzButtonModule
    ],
})
export class ModalCreatePauseComponent implements OnInit, OnDestroy {
    @Output() emitService = new EventEmitter();

    isLoading: boolean = true;
    isSaving: boolean = false;
    orderProcessId: number = 0;
    createdPauseId: number = 0;
    orderProcessDescription: string = '';
    pauseReasons: PauseReasonDto[] = [];
    currentUser: UserDto = new UserDto();
    previousStatusId: number = 0;
    machineId: number = 0;

    validateForm: FormGroup<{
        observations: FormControl<string>;
        selectedReason: FormControl<number | null>;
    }>;

    private unsubscribe$ = new Subject<void>();

    private readonly pauseService = inject(PauseService);
    private readonly pauseReasonsService = inject(PauseReasonsService);
    private readonly loggedService = inject(UserLoggedService);
    private readonly orderProcessService = inject(ProductionOrderProcessesService);
    private readonly modalRef = inject(NzModalRef);
    private readonly fb = inject(NonNullableFormBuilder);
    private readonly message = inject(NzMessageService);

    constructor(
        @Inject(NZ_MODAL_DATA)
        public data: {
            nzTitle: string;
            orderProcessId: number;
            orderProcessDescription: string;
            previousStatusId: number;
            machineId: number;
            pause?: ProductionPauseDto;
        }
    ) {
        this.validateForm = this.fb.group({
            observations: [''],
            selectedReason: [
                new FormControl<number | null>(null),
                [Validators.required]
            ],
        });
        this.orderProcessId = data.orderProcessId as number;
        this.orderProcessDescription = data.orderProcessDescription as string;
        this.previousStatusId = data.previousStatusId as number;
        this.machineId = data.machineId as number;
    }

    async ngOnInit(): Promise<void> {
        this.currentUser = this.loggedService.userLogged;

        await this.getPauseReasons();

        if (this.data.pause) {
            this.validateForm.controls.selectedReason.patchValue(
                this.data.pause.pauseReasonId,
            );
            this.validateForm.controls.observations.patchValue(
                this.data.pause.observations,
            );
        }

        this.isLoading = false;
    }

    ngOnDestroy(): void {
        this.pauseReasonsService.disconnectWebSocketByMachine(
            this.data.pause
            ? this.data.pause.machineId ? this.data.pause.machineId : this.machineId
            : this.machineId
        );
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    handleCancel(): void {
        this.emitService.next(null);
        this.modalRef.close();
    }

    async submitForm(): Promise<void> {
        try {
            this.isSaving = true;

            if (!this.validateForm.valid) {
                this.isSaving = false;
                this.message.error(`Porfavor, rellene los campos obligatorios`);
                return;
            }

            if (
                this.validateForm.value.selectedReason == undefined ||
                isNaN(Number(this.validateForm.value.selectedReason))
            ) {
                this.isSaving = false;
                this.message.error(`Porfavor, seleccione un motivo de pausa`);
                return;
            }

            const pause: ProductionPauseDto = {
                id: this.data.pause ? this.data.pause.id : undefined,
                startDate: this.data.pause
                    ? this.data.pause.startDate
                    : new Date(),
                endDate: this.data.pause ? this.data.pause.endDate : new Date(),
                observations: this.validateForm.value.observations ?? '',
                productionOrderProcessId: this.data.pause
                    ? this.data.pause.productionOrderProcessId
                    : this.orderProcessId,
                pauseReasonId:
                    Number(this.validateForm.value.selectedReason) ?? 0,
                pausedById: this.data.pause
                    ? this.data.pause.pausedById
                    : this.currentUser.id,
                previousStatusId: this.data.pause
                    ? this.data.pause.previousStatusId
                    : this.previousStatusId,
                machineId: this.data.pause
                ? this.data.pause.machineId ? this.data.pause.machineId : this.machineId
                : this.machineId,
                accepted: this.data.pause ? this.data.pause.accepted : false,
            };

            if (!this.data.pause) {
                const updatedOrderProcess: ProductionOrderProcessDto =
                    await this.orderProcessService.setOrderProcessAsPaused(
                        pause,
                    );
                this.emitService.next(updatedOrderProcess);
            } else {
                const productionPauseDto: ProductionPauseDto =
                    await this.pauseService.updatePause(pause);
                this.emitService.next(productionPauseDto);
            }

            this.isSaving = false;

            this.modalRef.close();
        } catch (e: unknown) {
            this.isSaving = false;
            if (e instanceof Error) {
                this.message.error(e.message);
            }
        }
    }

    private async getPauseReasons() {
        try {
            await this.pauseReasonsService.setupWebSocketByMachine(
                this.data.pause
                ? this.data.pause.machineId ? this.data.pause.machineId : this.machineId
                : this.machineId
            );
            this.pauseReasonsService.pauseReasonsByMachine$
                .pipe(takeUntil(this.unsubscribe$))
                .subscribe({
                    next: (pauseReasons) => {
                        this.pauseReasons = pauseReasons;
                        if (!this.data.pause) {
                            this.validateForm.controls.selectedReason.patchValue(
                                this.pauseReasons.length > 0
                                    ? this.pauseReasons[0].id
                                    : null,
                            );
                        }
                    },
                    error: () => {
                        this.message.error(
                            'No se han podido cargar los motivos de pausa.',
                        );
                    },
                });
        } catch (e: unknown) {
            if (e instanceof Error) {
                this.message.error(e.message);
            }
        }
    }
}
